import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

export type SegmentedOption<T extends string> = {
  value: T;
  label: ReactNode;
  icon?: LucideIcon;
  /** `warning`: the selected state is highlighted in amber (e.g. an edit mode). */
  tone?: 'default' | 'warning';
  disabled?: boolean;
  'data-testid'?: string;
};

export type SegmentedControlProps<T extends string> = {
  options: readonly SegmentedOption<T>[];
  value: T;
  onChange: (value: T) => void;
  'aria-label': string;
  size?: 'sm' | 'md';
  className?: string;
  'data-testid'?: string;
};

/** One choice among a few side by side (view mode, unit, source): the look of the app's tabs. */
export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  'aria-label': ariaLabel,
  size = 'md',
  className,
  'data-testid': testId,
}: SegmentedControlProps<T>) {
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      data-testid={testId}
      className={cn(
        'inline-flex w-fit items-center gap-0.5 rounded-lg bg-muted p-[3px] text-muted-foreground',
        size === 'sm' ? 'h-8' : 'h-9',
        className
      )}
    >
      {options.map(
        ({
          value: optionValue,
          label,
          icon: Icon,
          tone,
          disabled,
          'data-testid': optionTestId,
        }) => {
          const active = optionValue === value;
          return (
            <button
              key={optionValue}
              type="button"
              aria-pressed={active}
              disabled={disabled}
              data-testid={optionTestId}
              onClick={() => onChange(optionValue)}
              className={cn(
                'inline-flex h-full items-center justify-center gap-1.5 whitespace-nowrap rounded-md px-3 font-medium transition-[color,box-shadow] outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 [&_svg]:size-4 [&_svg]:shrink-0',
                size === 'sm' ? 'text-xs' : 'text-sm',
                active
                  ? tone === 'warning'
                    ? 'bg-warning/15 text-warning shadow-sm'
                    : 'bg-background text-foreground shadow-sm dark:bg-input/30'
                  : 'hover:text-foreground'
              )}
            >
              {Icon && <Icon aria-hidden />}
              {label}
            </button>
          );
        }
      )}
    </div>
  );
}
